import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import 'react-quill/dist/quill.core.css';

import './index.css';
import {
  DeleteOutlined,
  PlusOutlined,
  CaretDownOutlined,
  DownOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { getListOfCannedText } from '../../../Apis/Author';
import {
  getAllCannedText,
  targetEndPoints
} from '../../../Apis/Author';
import {
  formatDate as formatDateUtil,
  formatDateTime as formatDateTimeUtil
} from '../../../Utils/dateFormat';

import { Button, Card, Checkbox, Dropdown, Input, Menu, Modal, Select, Tooltip, notification } from 'antd';
import ConfirmationModal from '../ConfirmationModal';
import WarningIcon from '../../../Assets/Icons/WarningIcon';
import { FloatingMenu } from './FloatingMenu';
import {
  editSection,
  generateAiContent,
  acknowledgeAiGeneration,
  getAiJobSectionContent,
  markAiSectionAsAdded
} from '../../../Apis/Jobs';
import axiosInstance from '../../../Config/AxiosInstance';
import { useDispatch, useSelector } from 'react-redux';
import { AUTO_SAVE_DELAY } from '../../../Constants/StringConstants';
import { nanoid } from 'nanoid';
import { setUserUnsavedStatus } from '../../../Redux/Actions/User';
import { useLocation } from 'react-router-dom';

let dynamicHighlightWord = [];

export const setHighlightWordsForBlot = words => {
  dynamicHighlightWord = words;
};

const AI_SYSTEM_PROMPT =
  'IMPORTANT: Your response must be plain text only. Do not return JSON, markdown, code blocks, or any special characters. Return only a simple readable formatted (formatted if not explicitely said not to format in the prompt) string.\n\nFORMATTING RULES (always apply these unless you are explicitly told "do not change the formatting" or "return as-is"):\n- Reformat the output for readability after generating it.\n- Each numbered item (1., 2., 3. etc.) must start on its own new line with a blank line before it.\n- Sub-labels like "Plan:", "Assessment:", "Impression:", "Findings:" etc. must each start on a new line.\n- Each distinct clinical sentence or point should be on its own line.\n- Do not use bullet points (•, -, *) or any markdown symbols.\n- Do not use any bold or italic markdown formatting. Never wrap words in asterisks (e.g. **word** or *word*) or underscores (e.g. __word__ or _word_). Output plain text only.\n- Keep punctuation clean and sentences complete.\n\nCRITICAL DATA RULES:\n- You may receive clinical note data in the user message. Use it only if your instruction requires it.\n- If the prompt requires data from the clinical note and ALL of it is completely absent, respond with "No data available."\n- If your instruction requires data and SOME is present, return only what is available. Do not mention missing fields.\n- If your instruction does not require any data from the clinical note (e.g. you are told to return a fixed text), execute it directly without checking the note.\n- Never fabricate or infer data not explicitly present in the clinical note.';

const QuillEditor = (
  {
    sections,
    setCptCodes,
    headings,
    jobId,
    jobName,
    setSaveStatus,
    saveStatus,
    isHeading,
    setIsHeading,
    setLastUpdateTime,
    loading,
    setEditorLoading,
    deleteSectionHandler,
    deleteSectionFromEditor,
    setDeleteModalVisible,
    deleteModalVisible,
    highlightWord,
    setCptCodesLoader,
    matchedCodes,
    cptCodesLoader,
    setEditedSections,
    setParagraphs,
    activeSection,
    setActiveSection,
    changeStatus,
    setChangeStatus,
    actieSectionContent,
    setActiveSectionContent,
    viewOnly,
    localSectionIdMap,
    tabDataPatient,
    printInfoData,
    demographicsData,
    selectionVersion,
    patientDataSelection,
    patientData
  },
  ref
) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [changedSections, setChangeSections] = useState([]);
  const [headingMenuVisible, setHeadingMenuVisible] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [keyPhrases, setKeyPhrases] = useState([]);
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [k2pApisLoading, setK2pApisLoading] = useState(true); // Loading state for K2P dependent APIs
  const k2pLoadingTracker = useRef({
    cannedText: true,
    patientData: true,
    selection: true
  });
  const keyPhrasesDataRef = useRef({}); // Store full K2P objects keyed by id
  const keyPhrasesMapRef = useRef({}); // Store K2P objects keyed by lowercase key for type-to-expand
  const targetEndpointsRef = useRef([]); // Store target endpoints for dynamic fields
  const patientDataRef = useRef(null); // Store patient data for dynamic replacement
  const patientDataSelectionRef = useRef(null); // Store patient data selection (which items are checked/selected)
  const containerRef = useRef(null);
  const contentEditorsRef = useRef({});
  const cursorPositionRef = useRef({}); // Store cursor position for each editor
  const saveTimeoutRef = useRef(null); // Store debounce timeout
  const latestCardsDataRef = useRef([]); // Store latest cards data for debounced save
  const latestChangedSectionsRef = useRef([]); // Store latest changed sections for debounced save
  const changeCheckTimeoutRef = useRef(null); // Store change detection polling timeout
  const saveDataRef = useRef(null); // Store latest saveData function to avoid stale closures
  const saveInProgressRef = useRef(false); // Track if save API call is in progress
  const user = useSelector(state => state.userReducer);
  const dispatch = useDispatch();
  const location = useLocation();
  const [viewOnly1, setViewOnly1] = useState(false);
  const [aiModalState, setAiModalState] = useState({
    visible: false,
    cardId: null,
    heading: null,
    loading: false,
    result: null
  });
  const [aiDots, setAiDots] = useState(1);
  const [aiDisplayedText, setAiDisplayedText] = useState('');
  const [aiAcknowledged, setAiAcknowledged] = useState(null); // null = waiting, true/false from API
  const [aiAckChecked, setAiAckChecked] = useState(false);
  const [aiAckLoading, setAiAckLoading] = useState(false);
  const [aiSectionCache, setAiSectionCache] = useState({}); // keyed by normalized heading
  const [aiGlobalAcknowledged, setAiGlobalAcknowledged] = useState(false);
  const [aiPendingSections, setAiPendingSections] = useState([]); // sections to auto-fill, set only from GET response
  const aiAutoFillDoneRef = useRef(false);
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [deleteGroupConfirmId, setDeleteGroupConfirmId] = useState(null);
  const [deleteChildConfirm, setDeleteChildConfirm] = useState(null); // { groupId, childId }

  const updateK2pLoading = (key, value) => {
    k2pLoadingTracker.current[key] = value;
    const stillLoading = Object.values(k2pLoadingTracker.current).some(v => v);
    setK2pApisLoading(stillLoading);
  };

  // Initialize cards data from sections
  useEffect(() => {
    if (sections && sections.length > 0) {
      const mapSection = section => {
        let content = section.content.map(c => c.value).join('');
        content = content.replace(/\\n/g, '\n');
        content = content.replace(/\n/g, '<br>');
        // single &nbsp; between words makes the whole line unbreakable; keep runs (indentation)
        content = content.replace(/(?<!&nbsp;)(?<=\S)&nbsp;(?!&nbsp;)(?=\S)/g, ' ');
        const fieldType = section.heading.fieldType || 'TEXT';
        const rawOptions = section.options || [];
        const selectedValues = fieldType === 'CHECKBOX'
          ? rawOptions.filter(o => o.selected).map(o => o.label)
          : fieldType === 'DROPDOWN'
          ? (rawOptions.find(o => o.selected)?.label || null)
          : null;
        return {
          id: section.id,
          headingId: section.heading.headingId,
          heading: section.heading.customUIHeading.toUpperCase(),
          systemHeading: section.heading.systemHeading,
          submitToEmr: section.heading.submitToEmr,
          suppressOnEmpty: section.heading.suppressOnEmpty,
          paragraphs: content,
          fieldType,
          options: rawOptions,
          selectedValues,
        };
      };

      const groupMap = new Map();
      const order = [];

      sections.forEach(section => {
        const { groupId, groupName } = section.heading;
        if (groupId) {
          if (!groupMap.has(groupId)) {
            groupMap.set(groupId, {
              cardType: 'sectionGroup',
              id: groupId,
              title: groupName || groupId,
              children: [],
            });
            order.push({ type: 'group', id: groupId });
          }
          groupMap.get(groupId).children.push(mapSection(section));
        } else {
          order.push({ type: 'standalone', section });
        }
      });

      const cards = order.map(item =>
        item.type === 'group' ? groupMap.get(item.id) : mapSection(item.section)
      );
      setCardsData(cards);
    }
  }, [sections]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const currentJobStage = searchParams.get('jobStage');
    setViewOnly1(
      currentJobStage === 'in-review' || currentJobStage === 'completed'
    );
    const currentUserRole = searchParams.get('userRole');
    setIsIncomplete(
      currentJobStage === 'incomplete' ||
        currentJobStage === 'ready' ||
        currentUserRole === 'reviewer'
    );
  }, []);

  // Animate dots while AI modal is loading
  useEffect(() => {
    if (!aiModalState.loading) return;
    const interval = setInterval(() => {
      setAiDots(d => (d === 3 ? 1 : d + 1));
    }, 500);
    return () => clearInterval(interval);
  }, [aiModalState.loading]);

  // Show the full AI response instantly once received
  useEffect(() => {
    const fullText = aiModalState.result?.result?.[0] || '';
    setAiDisplayedText(aiModalState.loading ? '' : fullText);
  }, [aiModalState.result, aiModalState.loading]);

  // Fetch any AI-generated content already stored for this job (doctor/scribe in-progress only)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isDoctorInProgress =
      searchParams.get('jobStage') === 'in-progress' &&
      (searchParams.get('userRole') === 'doctor' ||
        searchParams.get('userRole') === 'scribe');
    if (!jobId || !isDoctorInProgress) return;
    aiAutoFillDoneRef.current = false;
    setAiPendingSections([]);
    getAiJobSectionContent(jobId).then(res => {
      if (!res.data?.success || !res.data.result?.length) return;
      const acknowledged = res.data.acknowledged ?? false;
      setAiGlobalAcknowledged(acknowledged);
      const sections = res.data.result[0]?.sections || [];
      const cache = {};
      sections.forEach(s => {
        cache[s.heading.toLowerCase()] = s;
      });
      setAiSectionCache(cache);
      // Only queue auto-fill when the server confirms the user has already acknowledged.
      // The Acknowledge button never sets aiPendingSections, so it cannot trigger auto-fill.
      if (acknowledged) {
        setAiPendingSections(sections);
      }
    }).catch(() => {});
  }, [jobId]);

  // Auto-fill sections once both GET data and cardsData are ready
  useEffect(() => {
    if (aiAutoFillDoneRef.current || !cardsData.length || !aiPendingSections.length) return;

    const normalizeHeading = h =>
      h.replace(/<[^>]*>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .trim();

    const toFill = [];
    cardsData.forEach(card => {
      if (card.cardType === 'sectionGroup') return;
      const key = normalizeHeading(card.heading).toLowerCase();
      const section = aiPendingSections.find(s => s.heading.toLowerCase() === key);
      if (section?.status === 'COMPLETED' && !section.added && section.response) {
        toFill.push({ card, section });
      }
    });

    if (!toFill.length) return;
    aiAutoFillDoneRef.current = true;

    const updatedCards = cardsData.map(c => {
      const entry = toFill.find(f => f.card.id === c.id);
      if (!entry) return c;
      return { ...c, paragraphs: entry.section.response.replace(/\n/g, '<br>') };
    });
    const filledIds = toFill.map(f => f.card.id);

    setCardsData(updatedCards);
    setChangeSections(prev => {
      const ids = new Set(prev);
      filledIds.forEach(id => ids.add(id));
      return [...ids];
    });
    setAiSectionCache(prev => {
      const updated = { ...prev };
      toFill.forEach(f => {
        const key = normalizeHeading(f.card.heading).toLowerCase();
        updated[key] = { ...updated[key], added: true };
      });
      return updated;
    });

    Promise.all(
      toFill.map(({ card }) =>
        markAiSectionAsAdded(jobId, normalizeHeading(card.heading))
      )
    )
      .then(() => saveDataRef.current(updatedCards, filledIds))
      .catch(() => {});
  }, [aiPendingSections, cardsData]);

  // Fetch canned text (key phrases) and target endpoints from API (doctor + in-progress only)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isDoctorInProgress =
      searchParams.get('jobStage') === 'in-progress' &&
      (searchParams.get('userRole') === 'doctor' ||
        searchParams.get('userRole') === 'scribe');

    if (!isDoctorInProgress) {
      updateK2pLoading('cannedText', false);
      return;
    }

    const fetchCannedTextAndEndpoints = async () => {
      try {
        // Fetch both K2P list and target endpoints in parallel
        const [cannedTextResponse, endpointsResponse] = await Promise.all([
          getAllCannedText('active'),
          targetEndPoints()
        ]);

        if (cannedTextResponse.data && cannedTextResponse.data.result) {
          // Store full K2P objects keyed by id for lookup
          const phraseDataMap = {};
          const keyToK2PMap = {};

          cannedTextResponse.data.result.forEach(item => {
            phraseDataMap[item.id] = item; // Store full object
            keyToK2PMap[item.key.toLowerCase()] = item; // Map by lowercase key for type-to-expand
          });

          keyPhrasesDataRef.current = phraseDataMap;
          keyPhrasesMapRef.current = keyToK2PMap;

          // Transform API data to dropdown options using id as value (must be unique)
          const phrases = cannedTextResponse.data.result.map(item => ({
            label: item.key,
            value: item.id
          }));
          setKeyPhrases(phrases);
        }

        if (endpointsResponse.data && endpointsResponse.data.result) {
          targetEndpointsRef.current = endpointsResponse.data.result;
        }
      } catch (error) {
        console.error('Error fetching canned text:', error);
      } finally {
        updateK2pLoading('cannedText', false);
      }
    };

    fetchCannedTextAndEndpoints();
  }, []);

  // Use patient data from parent (centralized fetch) for dynamic K2P replacement
  useEffect(() => {
    if (!patientData) return;

    // If patientData exists (even if not loaded yet or empty), process it
    if (patientData.loaded) {
      // Map patientData prop to the structure expected by K2P extraction
      patientDataRef.current = {
        vitalSign: patientData.vitals?.map(v => v.raw || v) || [],
        medDiagonsis: patientData.conditions?.map(c => c.raw || c) || [],
        patientDetails: {},
        allergies: patientData.allergies?.map(a => a.raw || a) || [],
        labReports: patientData.labReports?.map(l => l.raw || l) || [],
        medications: patientData.medications?.map(m => m.raw || m) || [],
        practitioners: [],
        payers: [],
        progressNotes: patientData.progressNotes?.map(p => p.raw || p) || []
      };

      console.log('K2P Patient data loaded from parent:', {
        vitalsCount: patientDataRef.current.vitalSign.length,
        conditionsCount: patientDataRef.current.medDiagonsis.length
      });

      updateK2pLoading('patientData', false);
    }
  }, [patientData]);

  // Use patient data selection from parent (centralized fetch)
  useEffect(() => {
    // Wait for patientDataSelection to be set (non-null means fetch is complete)
    if (patientDataSelection) {
      // Only set ref if there's actual data (not empty object)
      if (Object.keys(patientDataSelection).length > 0) {
        patientDataSelectionRef.current = patientDataSelection;
      }
      updateK2pLoading('selection', false);
    }
  }, [patientDataSelection]);

  // Helper function to format date
  const formatDate = (dateString, format = 'MM/DD/YYYY') => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      // Month names for DD MMM YYYY format
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];
      const monthName = monthNames[date.getMonth()];

      if (format === 'YYYY-MM-DD') {
        return `${year}-${month}-${day}`;
      }

      if (format === 'MM/DD/YYYY hh:mm A') {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // 0 should be 12
        const hoursStr = String(hours).padStart(2, '0');
        return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
      }

      if (format === 'YYYY/DD/MM hh:mm A') {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const hoursStr = String(hours).padStart(2, '0');
        return `${year}/${day}/${month} ${hoursStr}:${minutes} ${ampm}`;
      }

      if (format === 'DD MMM YYYY') {
        return `${day} ${monthName} ${year}`;
      }

      if (format === 'MMM DD, YYYY') {
        return `${monthName} ${day}, ${year}`;
      }

      if (format === 'DD MMM YYYY hh:mm A') {
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const hoursStr = String(hours).padStart(2, '0');
        return `${day} ${monthName} ${year} ${hoursStr}:${minutes} ${ampm}`;
      }

      return `${month}/${day}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Helper function to calculate age from birth date
  const calculateAge = birthDateString => {
    if (!birthDateString) return '';
    try {
      const birthDate = new Date(birthDateString);
      if (isNaN(birthDate.getTime())) return '';

      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      // Adjust age if birthday hasn't occurred this year
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age >= 0 ? `${age}` : '';
    } catch {
      return '';
    }
  };

  // Process dynamic placeholders in K2P phrase
  const processDynamicPlaceholders = k2pItem => {
    if (!k2pItem) return '';

    let phrase = k2pItem.phrase || '';

    const fallback = k2pItem.defaultValue || '';

    // Always replace ${AI Summary} placeholder regardless of dynamic/targetEndPoint status
    if (/\$\{AI Summary\}/gi.test(phrase)) {
      const rawAiSummary = patientDataSelectionRef.current?.aiSummary;
      const aiSummaryReplacement =
        rawAiSummary !== null &&
        rawAiSummary !== undefined &&
        rawAiSummary !== ''
          ? rawAiSummary
          : fallback;
      phrase = phrase.replace(/\$\{AI Summary\}/gi, aiSummaryReplacement);
    }

    // If not dynamic, return phrase as-is (matching mobile: if (item.isDynamic == true))
    if (!k2pItem.dynamic || !k2pItem.targetEndPoint) {
      return phrase;
    }

    const patientData = patientDataRef.current || {};
    const {
      vitalSign = [],
      medDiagonsis = [],
      patientDetails = {},
      allergies = [],
      labReports = [],
      medications = [],
      practitioners = [],
      payers = [],
      progressNotes = []
    } = patientData;

    // Get selected items from patient-data-selection API (right pane checkboxes)
    // Only expand checked/selected items for dynamic K2Ps
    const selection = patientDataSelectionRef.current;
    const selectedConditions = selection?.conditions
      ? selection.conditions.filter(c => c.selected).map(c => c.condition)
      : [];
    const selectedMedications = selection?.medications
      ? selection.medications.filter(m => m.selected).map(m => m.medication)
      : [];
    const selectedAllergies = selection?.allergies
      ? selection.allergies.map(a => a.allergyIntoleranceData)
      : allergies;
    const selectedLabReports = selection?.labReports
      ? selection.labReports.filter(l => l.selected).map(l => l.labReport)
      : [];
    // Observations (vitals): all are treated as selected
    // Progress notes: filter by selected progressNoteIds
    const selectedProgressNoteIds = selection?.progressNotes
      ? new Set(
          selection.progressNotes
            .filter(p => p.selected)
            .map(p => p.notes.progressNoteId)
        )
      : new Set();

    // Get available fields from target endpoint config (like mobile availableVitals, availableDemographics)
    const getAvailableFields = endpointName => {
      const endpoint = targetEndpointsRef.current?.find(
        e => e.fieldUiValue === endpointName
      );
      return endpoint?.avilableFields || endpoint?.availableFields || [];
    };

    // Get demographics from props as fallback
    const getDemographicValue = key => {
      if (demographicsData) {
        const item = demographicsData.find(
          d => d?.key?.toUpperCase() === key.toUpperCase()
        );
        return item?.value || fallback;
      }
      return fallback;
    };

    switch (k2pItem.targetEndPoint) {
      case 'GET_TODAYS_DATE': {
        // Check if phrase contains any date placeholders
        const hasTodaysDate =
          phrase.includes("{today's date}") || phrase.includes('{todaysdate}');
        const hasTodaysDateTime = phrase
          .toLowerCase()
          .includes("{today's date and time}");
        const hasEncounterDate =
          phrase.includes('{encounter date}') ||
          phrase.includes('{encounterdate}');
        const hasEncounterDateTime = phrase
          .toLowerCase()
          .includes('{encounter date and time}');

        if (
          !hasTodaysDate &&
          !hasTodaysDateTime &&
          !hasEncounterDate &&
          !hasEncounterDateTime
        ) {
          break;
        }

        // Replace today's date if present
        if (hasTodaysDate) {
          const today = formatDateUtil(new Date().toISOString(), fallback);
          phrase = phrase.replace(/\$\{today's date\}/gi, today);
          phrase = phrase.replace(/\$\{todaysdate\}/gi, today);
        }

        if (hasTodaysDateTime) {
          const todayDateTime = formatDateTimeUtil(new Date().toISOString(), fallback);
          phrase = phrase.replace(/\$\{today's date and time\}/gi, todayDateTime);
        }

        if (hasEncounterDate) {
          const encounterDateRaw =
            getDemographicValue('DATE OF VISIT') ||
            tabDataPatient?.patientdetails?.[0]?.admissionDate ||
            null;
          const encounterDate = formatDateUtil(encounterDateRaw, fallback);
          phrase = phrase.replace(/\$\{encounter date\}/gi, encounterDate);
          phrase = phrase.replace(/\$\{encounterdate\}/gi, encounterDate);
        }

        if (hasEncounterDateTime) {
          const encounterDateTimeRaw =
            getDemographicValue('DATE OF VISIT') || null;
          const encounterDateTimeFormatted = formatDateTimeUtil(
            encounterDateTimeRaw,
            fallback
          );
          phrase = phrase.replace(
            /\$\{encounter date and time\}/gi,
            encounterDateTimeFormatted
          );
        }
        break;
      }

      case 'GET_ADMISSION_DATE': {
        const hasAdmissionDate =
          phrase.includes('{admissionDate}') ||
          phrase.includes('{admissiondate}');
        const hasAdmissionDateTime =
          phrase.toLowerCase().includes('{admissiondatetime}');

        if (!hasAdmissionDate && !hasAdmissionDateTime) {
          break;
        }

        if (hasAdmissionDate) {
          const admissionDate =
            patientDetails.admissionDate ||
            tabDataPatient?.patientdetails?.[0]?.admissionDate;
          const admissionDateFormatted = formatDateUtil(admissionDate, fallback);
          phrase = phrase.replace(/\$\{admissionDate\}/gi, admissionDateFormatted);
        }

        if (hasAdmissionDateTime) {
          const admissionDateTimeRaw =
            patientDetails.admissionDateTime ||
            tabDataPatient?.patientdetails?.[0]?.admissionDateTime;
          const admissionDateTimeFormatted = formatDateTimeUtil(
            admissionDateTimeRaw,
            fallback
          );
          phrase = phrase.replace(
            /\$\{admissionDateTime\}/gi,
            admissionDateTimeFormatted
          );
        }
        break;
      }

      case 'GET_ADMISSION_DATE_TIME': {
        if (
          !phrase.includes('{admissionDateTime}') &&
          !phrase.includes('{admissiondatetime}')
        ) {
          break;
        }
        const admissionDateTimeRaw =
          patientDetails.admissionDateTime ||
          tabDataPatient?.patientdetails?.[0]?.admissionDateTime;
        const admissionDateTimeFormatted = formatDateTimeUtil(
          admissionDateTimeRaw,
          fallback
        );
        phrase = phrase.replace(
          /\$\{admissionDateTime\}/gi,
          admissionDateTimeFormatted
        );
        break;
      }

      case 'GET_LAST_VITALS': {
        // Mobile check: if (_vitals.isNotEmpty)
        if (!vitalSign || vitalSign.length === 0) {
          phrase = phrase + ' ' + fallback;
          break;
        }

        // Create a map of vital types to their formatted values
        // Mobile format: value unit | Method: method | Recorded At: date
        // New API uses recordedDate instead of recordedAt
        // Blood pressure uses systolicValue/diastolicValue instead of value
        // API vital types: weight, temperature, heartrate, bloodPressure, respirations, bloodSugar, height, oxygenSaturation, painLevel
        const vitalMap = {};
        vitalSign.forEach(vital => {
          const originalType = vital.type || '';
          const typeLower = originalType.toLowerCase();
          const method = vital.method || fallback;
          const recordedAt = vital.recordedDate
            ? formatDate(vital.recordedDate, 'YYYY-MM-DD')
            : fallback;

          let formattedValue;

          // Handle blood pressure specially (uses systolicValue/diastolicValue)
          if (typeLower === 'bloodpressure') {
            if (vital.systolicValue != null && vital.diastolicValue != null) {
              const bpValue = `${vital.systolicValue}/${vital.diastolicValue}`;
              const unit = vital.unit || 'mmHg';
              formattedValue = `${bpValue} ${unit} | Method: ${method} | Recorded At: ${recordedAt}`;
            } else {
              formattedValue = fallback;
            }
          } else if (vital.value) {
            const value = vital.value;
            const unit = vital.unit || '';
            formattedValue = `${value} ${unit} | Method: ${method} | Recorded At: ${recordedAt}`;
          } else {
            formattedValue = fallback;
          }

          // Store with both original type and lowercase for flexible matching
          vitalMap[originalType] = formattedValue;
          vitalMap[typeLower] = formattedValue;
        });

        // Use availableFields from target endpoint config (like mobile availableVitals)
        const availableVitals = getAvailableFields('GET_LAST_VITALS');
        // Default fields include both camelCase (from API) and lowercase variants
        const vitalFieldsToProcess =
          availableVitals.length > 0
            ? availableVitals
            : [
                'heartrate',
                'heartRate',
                'bloodpressure',
                'bloodPressure',
                'temperature',
                'respirations',
                'weight',
                'height',
                'bloodsugar',
                'bloodSugar',
                'painlevel',
                'painLevel',
                'oxygensaturation',
                'oxygenSaturation'
              ];

        // Mobile: for (final element in availableVitals) { splitList = splitVitalText(text, element); ... }
        vitalFieldsToProcess.forEach(field => {
          const fieldLower = field.toLowerCase();
          // Only process if phrase contains this placeholder (case-insensitive check)
          if (phrase.toLowerCase().includes(`{${fieldLower}}`)) {
            // Replace all case variants of the placeholder
            const regex = new RegExp(`\\$\\{${field}\\}`, 'gi');
            phrase = phrase.replace(
              regex,
              vitalMap[fieldLower] || vitalMap[field] || fallback
            );
          }
        });
        break;
      }

      case 'GET_MED_DIAGONOSIS': {
        // Mobile check: if (item.phrase!.contains('{medDiagonsis}'))
        const hasMedDiagnosis =
          phrase.includes('{medDiagonsis}') ||
          phrase.includes('{meddiagonsis}');
        const hasMedDiagnosisWithICD10 = phrase
          .toLowerCase()
          .includes('{meddiagnosiswithicd10code}');

        if (!hasMedDiagnosis && !hasMedDiagnosisWithICD10) {
          break;
        }
        // Use only selected conditions from patient-data-selection (right pane checkboxes)
        if (selectedConditions && selectedConditions.length > 0) {
          if (hasMedDiagnosis) {
            const diagnosisList = selectedConditions
              .map((d, index) => {
                const desc = d.icd10Description || 'Unknown';
                return `<p>${index + 1}. ${desc}</p>`;
              })
              .join('<p><br></p>');
            phrase = phrase.replace(/\$\{medDiagonsis\}/gi, diagnosisList);
          }
          if (hasMedDiagnosisWithICD10) {
            const diagnosisWithICD10List = selectedConditions
              .map((d, index) => {
                const desc = d.icd10Description || 'Unknown';
                const icd10 = d.icd10 || '';
                return `<p>${index + 1}. ${desc}${
                  icd10 ? ` - ICD10: ${icd10}` : ''
                }</p>`;
              })
              .join('<p><br></p>');
            phrase = phrase.replace(
              /\$\{medDiagnosisWIthICD10Code\}/gi,
              diagnosisWithICD10List
            );
          }
        } else {
          phrase = phrase.replace(/\$\{medDiagonsis\}/gi, fallback);
          phrase = phrase.replace(
            /\$\{medDiagnosisWIthICD10Code\}/gi,
            fallback
          );
        }
        break;
      }

      case 'GET_MED_DIAGNOSIS_WITH_ICD10_CODE': {
        if (
          !phrase.includes('{medDiagnosisWIthICD10Code}') &&
          !phrase.includes('{meddiagnosiswithicd10code}')
        ) {
          break;
        }
        if (selectedConditions && selectedConditions.length > 0) {
          const diagnosisList = selectedConditions
            .map((d, index) => {
              const desc = d.icd10Description || 'Unknown';
              const icd10 = d.icd10 || '';
              return `<p>${index + 1}. ${desc}${
                icd10 ? ` - ICD10: ${icd10}` : ''
              }</p>`;
            })
            .join('<p><br></p>');

          phrase = phrase.replace(
            /\$\{medDiagnosisWIthICD10Code\}/gi,
            diagnosisList
          );
        } else {
          phrase = phrase.replace(
            /\$\{medDiagnosisWIthICD10Code\}/gi,
            fallback
          );
        }
        break;
      }

      case 'GET_DEMOGRAPHICS': {
        // Calculate age from birthDate
        const birthDateForAge =
          patientDetails.birthDate ||
          tabDataPatient?.patientdetails?.[0]?.birthDate;
        const calculatedAge = calculateAge(birthDateForAge);

        // Find primary care physician from practitioners (relation: "Primary")
        const allPractitioners =
          practitioners.length > 0
            ? practitioners
            : tabDataPatient?.practioners?.[0]?.data || [];
        const primaryPhysician = allPractitioners.find(
          p => p.relation?.toLowerCase() === 'primary'
        );
        const pcpName = primaryPhysician
          ? `${primaryPhysician.firstName || ''} ${
              primaryPhysician.lastName || ''
            }`.trim() || fallback
          : fallback;
        const pcpPhone = primaryPhysician
          ? primaryPhysician.officePhone ||
            primaryPhysician.cellPhone ||
            primaryPhysician.homePhone ||
            fallback
          : fallback;

        // Find primary payer from coverages (payerRank: "Primary")
        const allPayers =
          payers.length > 0
            ? payers
            : tabDataPatient?.coverages?.[0]?.payers || [];
        const primaryPayerObj = allPayers.find(
          p => p.payerRank?.toLowerCase() === 'primary'
        );
        const primaryPayerName = primaryPayerObj?.payerName || fallback;

        // Build demographics map
        const demoMap = {
          patientname:
            patientDetails.firstName && patientDetails.lastName
              ? `${patientDetails.firstName} ${patientDetails.lastName}`
              : getDemographicValue('NAME'),
          age: calculatedAge,
          gender: patientDetails.gender || getDemographicValue('GENDER'),
          dateofbirth:
            patientDetails.birthDate || getDemographicValue('DATE OF BIRTH'),
          bedid:
            patientDetails.bedId ||
            tabDataPatient?.patientdetails?.[0]?.bedId ||
            fallback,
          beddesc:
            patientDetails.bedDesc ||
            tabDataPatient?.patientdetails?.[0]?.bedDesc ||
            fallback,
          roomdesc:
            patientDetails.roomDesc ||
            tabDataPatient?.patientdetails?.[0]?.roomDesc ||
            fallback,
          roomid:
            String(
              patientDetails.roomId ??
                tabDataPatient?.patientdetails?.[0]?.roomId ??
                ''
            ) || fallback,
          admissiondate:
            patientDetails.admissionDate ||
            tabDataPatient?.patientdetails?.[0]?.admissionDate ||
            fallback,
          dischargedate: formatDateUtil(
            patientDetails.dischargeDate ||
              tabDataPatient?.patientdetails?.[0]?.dischargeDate,
            fallback
          ),
          medicalrecordnumber:
            patientDetails.medicalRecordNumber ||
            tabDataPatient?.patientdetails?.[0]?.medicalRecordNumber ||
            getDemographicValue('MEDICAL RECORD#'),
          primarycarephysicianname: pcpName,
          primarycarephysicianphone: pcpPhone,
          primarypayer: primaryPayerName
        };

        // Use availableFields from target endpoint config (like mobile availableDemographics)
        const availableDemographics = getAvailableFields('GET_DEMOGRAPHICS');
        const fieldsToProcess =
          availableDemographics.length > 0
            ? availableDemographics
            : Object.keys(demoMap);

        // Mobile: for (final element in availableDemographics) { splitList = splitVitalText(text, element); ... }
        fieldsToProcess.forEach(field => {
          const fieldLower = field.toLowerCase();
          // Only process if phrase contains this placeholder
          if (phrase.toLowerCase().includes(`{${fieldLower}}`)) {
            const regex = new RegExp(`\\$\\{${field}\\}`, 'gi');
            phrase = phrase.replace(regex, demoMap[fieldLower] || fallback);
          }
        });

        if (phrase.toLowerCase().includes('{dischargedatetime}')) {
          const dischargeDateTimeRaw =
            patientDetails.dischargeDateTime ||
            tabDataPatient?.patientdetails?.[0]?.dischargeDateTime;
          const dischargeDateTimeFormatted = formatDateTimeUtil(
            dischargeDateTimeRaw,
            fallback
          );
          phrase = phrase.replace(
            /\$\{dischargeDateTime\}/gi,
            dischargeDateTimeFormatted
          );
        }
        break;
      }

      case 'GET_ALLERGY': {
        // Mobile check: if (item.phrase!.contains('{allergies}'))
        if (!phrase.includes('{allergies}')) {
          break;
        }
        // Use only selected allergies from patient-data-selection (right pane checkboxes)
        if (selectedAllergies && selectedAllergies.length > 0) {
          // Mobile format: "Allergen: allergen1, allergen2, allergen3"
          const allergyList = selectedAllergies
            .map(a => a.allergen || 'Unknown')
            .join(', ');
          phrase = phrase.replace(
            /\$\{allergies\}/gi,
            `Allergen: ${allergyList}`
          );
        } else {
          phrase = phrase.replace(/\$\{allergies\}/gi, fallback);
        }
        break;
      }

      case 'GET_MEDICATION': {
        const hasMedication = phrase.includes('{medication}');
        const hasMedicationNames = phrase
          .toLowerCase()
          .includes('{medication names}');
        if (!hasMedication && !hasMedicationNames) {
          break;
        }
        // Use only selected medications from patient-data-selection (right pane checkboxes)
        if (selectedMedications && selectedMedications.length > 0) {
          if (hasMedication) {
            // Format medications list with HTML line breaks for Quill
            // Each medication has: Name, Dose, Frequency, Start Date Time
            // Use <br> for line breaks within medication, </p><p><br></p><p> for blank line between medications
            const formattedMeds = selectedMedications
              .map(med => {
                const name = med.description || 'Unknown medication';
                const schedule = (med.schedules || [])[0] || {};
                const dose = schedule.dose || '';
                const doseUOM = schedule.doseUOM || '';
                const frequency = schedule.frequency || '';
                const startDate = schedule.startDateTime
                  ? formatDate(schedule.startDateTime, 'MMM DD, YYYY')
                  : fallback;

                return `- ${[name, dose, doseUOM, frequency, startDate]
                  .filter(Boolean)
                  .join(' ')}`;
              })
              .join('</p><p>');

            phrase = phrase.replace(/\$\{medication\}/gi, formattedMeds);
          }

          if (hasMedicationNames) {
            // Format as numbered list of medication names only
            const formattedMedNames = selectedMedications
              .map(
                (med, index) =>
                  `${index + 1}. ${med.description || 'Unknown medication'}`
              )
              .join('</p><p>');

            phrase = phrase.replace(
              /\$\{medication names\}/gi,
              formattedMedNames
            );
          }
        } else {
          if (hasMedication) {
            phrase = phrase.replace(/\$\{medication\}/gi, fallback);
          }
          if (hasMedicationNames) {
            phrase = phrase.replace(/\$\{medication names\}/gi, fallback);
          }
        }
        break;
      }

      case 'GET_LAB_REPORTS': {
        // Mobile check: if (item.phrase!.contains('{labReport}'))
        if (
          !phrase.includes('{labReport}') &&
          !phrase.includes('{labreport}')
        ) {
          break;
        }
        // Use only selected lab reports from patient-data-selection (right pane checkboxes)
        if (selectedLabReports && selectedLabReports.length > 0) {
          // Format lab reports with HTML for Quill rendering
          // Use <br> for line breaks within a report, </p><p><br></p><p> for blank lines
          const formattedReports = selectedLabReports.map(report => {
            const effectiveDate = report.effectiveDateTime
              ? formatDate(report.effectiveDateTime, 'YYYY-MM-DD')
              : fallback;
            const issuedDate = report.issuedDateTime
              ? formatDate(report.issuedDateTime, 'YYYY-MM-DD')
              : fallback;
            const performingLab = report.performingLaboratory || fallback;
            const reportingLab = report.reportingLaboratory || fallback;

            let reportString = `Effective Date: ${effectiveDate}`;
            reportString += `<br>Issued Date: ${issuedDate}`;
            reportString += `<br>Performing Laboratory: ${performingLab}`;
            reportString += `<br>Reporting Laboratory: ${reportingLab}`;

            // Add test sets and results (blank line before each panel)
            const testSets = report.testSet || [];
            testSets.forEach(testSet => {
              reportString += `</p><p><br></p><p>Panel Name: ${
                testSet.panelName || fallback
              }`;

              const results = testSet.results || [];
              results.forEach(result => {
                const value = result.valueQuantity?.value || fallback;
                const unit = result.valueQuantity?.unitText || '';
                const desc = result.codeDescription;
                const descPart =
                  desc && desc !== 'TEST NAME NOT PROVIDED'
                    ? `Desc: ${desc} `
                    : '';
                const name = result.observationName || fallback;
                reportString += `<br>${descPart}Name: ${name} Result: ${value} ${unit}`;
                if (typeof result.comment === 'string' && result.comment) {
                  reportString += `<br>Comment: ${result.comment}`;
                }
              });
            });

            return reportString;
          });

          phrase = phrase.replace(
            /\$\{labReport\}/gi,
            formattedReports.join('</p><p><br></p><p>')
          );
        } else {
          phrase = phrase.replace(/\$\{labReport\}/gi, fallback);
        }
        break;
      }

      case 'GET_PROGRESS_NOTE':
      case 'GET_PROGRESS_NOTE_WITH_HEADING': {
        // Filter progress notes by selection if available (right pane checkboxes)
        // Use ?? (nullish coalescing) instead of || because progressNoteId can be 0 (falsy but valid)
        const filteredProgressNotes = selectedProgressNoteIds
          ? progressNotes.filter(note => {
              const noteId = note.progressNoteId ?? note.id;
              return (
                noteId !== undefined &&
                noteId !== null &&
                selectedProgressNoteIds.has(noteId)
              );
            })
          : progressNotes;

        // Find the most recent progress note with section data
        const noteWithSections = filteredProgressNotes.find(
          note => note.jobForProgressNote?.sectionObjects?.length > 0
        );

        if (
          !noteWithSections ||
          !noteWithSections.jobForProgressNote?.sectionObjects
        ) {
          // No progress notes available, replace all placeholders with N/A
          phrase = phrase.replace(
            /\$\{Complete Note\}/gi,
            'No previous notes available'
          );
          phrase = phrase.replace(/\$\{[^}]+\}/gi, fallback);
          break;
        }

        const noteSections = noteWithSections.jobForProgressNote.sectionObjects;
        const includeHeadingInPlaceholders =
          k2pItem.targetEndPoint === 'GET_PROGRESS_NOTE_WITH_HEADING';

        // Helper to strip HTML tags
        const stripHtml = html => {
          if (!html) return '';
          const temp = document.createElement('div');
          temp.innerHTML = html;
          return temp.textContent || temp.innerText || '';
        };

        // Helper to get heading text (strip HTML if present)
        const getHeadingText = heading => {
          return stripHtml(
            heading?.customUIHeading || heading?.systemHeading || 'Unknown'
          );
        };

        // Helper function to find section with EXACT case-insensitive match (like mobile checkHeading)
        const findSectionByExactMatch = placeholder => {
          const searchTerm = placeholder.toLowerCase();
          return noteSections.find(section => {
            const customHeading = stripHtml(
              section.heading?.customUIHeading || ''
            ).toLowerCase();
            return customHeading === searchTerm;
          });
        };

        // Handle ${Complete Note} - combine all sections
        // Note: In mobile app, Complete Note ALWAYS includes heading in both modes
        // The only difference is spacing (extra newline in withHeading mode)
        if (
          phrase.includes('${Complete Note}') ||
          phrase.includes('${complete note}')
        ) {
          const separator = includeHeadingInPlaceholders ? '<p><br></p>' : '';
          const completeNote = noteSections
            .map(section => {
              const headingText = getHeadingText(section.heading);
              const contentHtml =
                section.content?.map(c => c.value || '').join('') || '';

              // Check if content has any actual text
              const hasText = contentHtml.replace(/<[^>]*>/g, '').trim();
              if (!hasText) return null;

              // Always include heading for Complete Note (matching mobile behavior)
              return `<p>${headingText}</p>${contentHtml}`;
            })
            .filter(Boolean)
            .join(separator);

          phrase = phrase.replace(
            /\$\{Complete Note\}/gi,
            completeNote || 'No content available'
          );
        }

        // Handle ${Complete Note: Only EHR Submitted} - same as ${Complete Note} but:
        // 1. Skip sections where submitToEmr is false
        // 2. Skip sections where suppressOnEmpty is true and content is empty
        if (
          phrase.includes('${Complete Note: Only EHR Submitted}') ||
          phrase.includes('${complete note: only ehr submitted}')
        ) {
          const separator = includeHeadingInPlaceholders ? '<p><br></p>' : '';
          const ehrNote = noteSections
            .map(section => {
              if (section.heading?.submitToEmr === false) return null;

              const contentHtml =
                section.content?.map(c => c.value || '').join('') || '';
              const hasText = contentHtml.replace(/<[^>]*>/g, '').trim();

              if (section.heading?.suppressOnEmpty === true && !hasText)
                return null;

              if (!hasText) return null;

              const headingText = getHeadingText(section.heading);
              return `<p>${headingText}</p>${contentHtml}`;
            })
            .filter(Boolean)
            .join(separator);

          phrase = phrase.replace(
            /\$\{Complete Note: Only EHR Submitted\}/gi,
            ehrNote || 'No content available'
          );
        }

        // Handle section-specific placeholders (e.g., ${Allergies}, ${past medical}, etc.)
        // Use EXACT case-insensitive match like mobile app's checkHeading function
        const placeholderRegex = /\$\{([^}]+)\}/gi;
        let match;
        while ((match = placeholderRegex.exec(k2pItem.phrase)) !== null) {
          const placeholder = match[1];
          if (placeholder.toLowerCase() === 'complete note') continue;
          if (
            placeholder.toLowerCase() === 'complete note: only ehr submitted'
          )
            continue;

          // Find matching section by EXACT case-insensitive match (like mobile checkHeading)
          const matchingSection = findSectionByExactMatch(placeholder);

          if (matchingSection) {
            const headingText = getHeadingText(matchingSection.heading);
            const contentHtml =
              matchingSection.content?.map(c => c.value || '').join('') ||
              `<p>${fallback}</p>`;

            let replacement;
            if (includeHeadingInPlaceholders) {
              // GET_PROGRESS_NOTE_WITH_HEADING: heading + content
              replacement = `<p>${headingText}</p>${contentHtml}`;
            } else {
              // GET_PROGRESS_NOTE: content only
              replacement = contentHtml;
            }

            const placeholderPattern = new RegExp(
              `\\$\\{${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`,
              'gi'
            );
            phrase = phrase.replace(placeholderPattern, replacement);
          } else {
            // No matching section found - replace with fallback
            const placeholderPattern = new RegExp(
              `\\$\\{${placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`,
              'gi'
            );
            phrase = phrase.replace(placeholderPattern, fallback);
          }
        }
        break;
      }

      case 'GET_AI_SUMMARY': {
        const aiSummaryValue = selection?.aiSummary;
        const aiSummary =
          aiSummaryValue !== null &&
          aiSummaryValue !== undefined &&
          aiSummaryValue !== ''
            ? aiSummaryValue
            : fallback;
        phrase = phrase.replace(/\$\{AI Summary\}/gi, aiSummary);
        break;
      }

      default:
        break;
    }

    return phrase;
  };

  // Get filtered key phrases for a specific card based on template restrictions
  const getFilteredKeyPhrasesForCard = cardHeading => {
    const jobTemplateId = printInfoData?.[0]?.dictationTemplate?.id;

    // Strip HTML from card heading for comparison
    const stripHtml = html => {
      if (!html) return '';
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return (temp.textContent || temp.innerText || '').trim();
    };

    const cleanHeading = stripHtml(cardHeading);

    return keyPhrases.filter(phraseOption => {
      const k2pItem = keyPhrasesDataRef.current[phraseOption.value];
      if (!k2pItem) return true;

      // If not restricted to templates, show in all sections
      if (!k2pItem.restrictedToTemplates) {
        return true;
      }

      // restrictedToTemplates is true - check templateMapping
      if (!k2pItem.templateMapping || k2pItem.templateMapping.length === 0) {
        return false;
      }

      if (!jobTemplateId) {
        return false;
      }

      // Find matching template in templateMapping
      const matchingTemplate = k2pItem.templateMapping.find(
        mapping => mapping.templateId === jobTemplateId
      );

      if (!matchingTemplate) {
        return false;
      }

      // If allHeadings is true, show in all sections
      if (matchingTemplate.allHeadings) {
        return true;
      }

      // allHeadings is false - check if card heading matches templateHeadings
      if (
        !matchingTemplate.templateHeadings ||
        matchingTemplate.templateHeadings.length === 0
      ) {
        return false;
      }

      // Case-insensitive match between card heading and templateHeadings
      return matchingTemplate.templateHeadings.some(
        th => th.toLowerCase() === cleanHeading.toLowerCase()
      );
    });
  };

  // Close heading menu when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        headingMenuVisible &&
        !event.target.closest('.floating-menu-container')
      ) {
        setHeadingMenuVisible(null);
      }
    };

    if (headingMenuVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [headingMenuVisible]);

  // Convert Quill's HTML output to plain text with <br> tags (no <p> tags)
  const convertHtmlToPlainWithBr = html => {
    if (!html) return '';

    let result = html;

    // Step 1: Remove <br> from empty paragraphs (Quill uses <p><br></p> as placeholder for empty lines)
    // The paragraph boundary itself will be converted to <br> in step 2
    result = result.replace(/<p><br><\/p>/g, '<p></p>');

    // Step 2: Convert paragraph transitions to <br>
    result = result.replace(/<\/p><p>/g, '<br>');

    // Step 3: Remove leading <p> and trailing </p>
    result = result.replace(/^<p>/, '');
    result = result.replace(/<\/p>$/, '');

    return result;
  };

  // Handle AI Generate button click
  const handleAiGenerate = card => {
    const sectionHeadingPlain = card.heading
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .trim();
    const cacheKey = sectionHeadingPlain.toLowerCase();
    const cached = aiSectionCache[cacheKey];

    // If a completed result is stored server-side, show it instantly without a new POST
    if (cached?.status === 'COMPLETED' && !cached.added && cached.response) {
      setAiModalState({
        visible: true,
        cardId: card.id,
        heading: sectionHeadingPlain,
        loading: false,
        result: { result: [cached.response] }
      });
      setAiAcknowledged(aiGlobalAcknowledged ? true : false);
      setAiAckChecked(false);
      return;
    }

    const matchedHeading = headings?.find(
      h => h.customUIHeading.toLowerCase() === cacheKey
    );
    const aiPrompt = matchedHeading?.aiPrompt || '';

    const getSectionContent = c => {
      if (c.fieldType === 'CHECKBOX') return (c.selectedValues || []).join(', ');
      if (c.fieldType === 'DROPDOWN') return c.selectedValues || '';
      return convertHtmlToPlainWithBr(c.paragraphs);
    };
    const sectionsJson = cardsData.flatMap(c => {
      if (c.cardType === 'sectionGroup') {
        return (c.children || []).map(child => ({
          heading: (child.heading || '').replace(/<[^>]*>/g, '').trim(),
          content: getSectionContent(child)
        }));
      }
      return [{ heading: (c.heading || '').replace(/<[^>]*>/g, '').trim(), content: getSectionContent(c) }];
    });

    const messages = [
      { role: 'system', content: AI_SYSTEM_PROMPT },
      {
        role: 'user',
        content: `${aiPrompt}\n\nGenerate content for the "${sectionHeadingPlain}" section based on the following complete clinical note (all sections as JSON):\n\n${JSON.stringify(
          sectionsJson
        )}`
      }
    ];

    setAiModalState({
      visible: true,
      cardId: card.id,
      heading: sectionHeadingPlain,
      loading: true,
      result: null
    });
    setAiDots(1);
    setAiAcknowledged(null);
    setAiAckChecked(false);

    generateAiContent(jobId, sectionHeadingPlain, messages)
      .then(response => {
        setAiModalState(prev => ({
          ...prev,
          loading: false,
          result: response.data
        }));
        setAiAcknowledged(response.data?.acknowledged ?? false);
        // Update cache with freshly generated result
        if (response.data?.success && response.data.result?.[0]) {
          setAiSectionCache(prev => ({
            ...prev,
            [cacheKey]: {
              heading: sectionHeadingPlain,
              response: response.data.result[0],
              status: 'COMPLETED',
              added: false
            }
          }));
        }
      })
      .catch(() => {
        setAiModalState(prev => ({ ...prev, loading: false }));
      });
  };

  // Save data
  const saveData = (data, sectionsToSave) => {
    // Use ref values if not provided
    const currentChangedSections =
      sectionsToSave || latestChangedSectionsRef.current;

    if (
      loading ||
      currentChangedSections.length === 0 ||
      viewOnly ||
      viewOnly1 ||
      saveInProgressRef.current
    ) {
      return;
    }
    if (!data) {
      data = cardsData;
    }

    // Mark save as in progress and clear the ref immediately to prevent duplicate saves
    saveInProgressRef.current = true;
    latestChangedSectionsRef.current = [];
    setSaveStatus('Saving');

    const buildSectionPayload = (card, groupInfo = {}) => {
      const isCheckbox = card.fieldType === 'CHECKBOX';
      const isDropdown = card.fieldType === 'DROPDOWN';
      const section = {
        id: card.id,
        heading: {
          customUIHeading: convertHtmlToPlainWithBr(card.heading),
          systemHeading: card.systemHeading || 'NONE',
          submitToEmr: card.submitToEmr,
          suppressOnEmpty: card.suppressOnEmpty ?? false,
          fieldType: card.fieldType || 'TEXT',
          ...(card.headingId && { headingId: card.headingId }),
          ...(groupInfo.groupId && { groupId: groupInfo.groupId, groupName: groupInfo.groupName || null }),
        },
        content: [
          {
            type: 'text',
            value: isCheckbox || isDropdown ? '' : convertHtmlToPlainWithBr(card.paragraphs)
          }
        ]
      };
      if (isCheckbox) {
        section.options = (card.options || []).map(o => {
          const label = typeof o === 'string' ? o : o.label;
          return { label, selected: (card.selectedValues || []).includes(label) };
        });
      } else if (isDropdown) {
        section.options = (card.options || []).map(o => {
          const label = typeof o === 'string' ? o : o.label;
          return { label, selected: label === card.selectedValues };
        });
      }
      return section;
    };

    const sectionsPayload = data.flatMap(card =>
      card.cardType === 'sectionGroup'
        ? (card.children || []).map(child => buildSectionPayload(child, { groupId: card.id, groupName: card.title }))
        : [buildSectionPayload(card)]
    );

    let newSections = sectionsPayload.filter(item => item.id.startsWith('new'));
    const payload = {
      sections: sectionsPayload,
      sectionIds: Array.from(new Set(currentChangedSections))
    };
    setEditedSections(payload.sections);

    editSection(jobId, payload, user.sessionId)
      .then(response => {
        saveInProgressRef.current = false;
        if (response.data.success) {
          setChangeSections([]);
          setSaveStatus('Saved');
          dispatch(setUserUnsavedStatus(false));

          if (response.data.result) {
            if (response.data.result[0].newSection) {
              const newSectionsId = response.data.result[0].newSectionsId || [];
              let newSectionsIdIndex = 0;
              const replaceNewId = card => {
                if (card.id && card.id.startsWith('new-') && newSectionsIdIndex < newSectionsId.length) {
                  const newId = newSectionsId[newSectionsIdIndex];
                  localSectionIdMap.set(card.id, newId);
                  newSectionsIdIndex++;
                  return { ...card, id: newId };
                }
                return card;
              };
              setCardsData(prevCards =>
                prevCards.map(card => {
                  if (card.cardType === 'sectionGroup') {
                    return { ...card, children: (card.children || []).map(replaceNewId) };
                  }
                  return replaceNewId(card);
                })
              );
            }

            setLastUpdateTime(response.data.result[0].lastUpdatedTime);

            const cptCodes = response.data.result[0].cptCodesInContent.map(
              item => ({
                cptCode: item.cptCode,
                description: item.description,
                category: item.category,
                content: item.content
              })
            );

            setCptCodesLoader(prev => !prev);
            setCptCodes(cptCodes);
          }
        } else {
          setSaveStatus(null);
          notification['error']({
            message: 'Alert',
            description: response.data.message
          });
        }
      })
      .catch(err => {
        saveInProgressRef.current = false;
        console.log(err);
        setSaveStatus('You are Offline');
        // Restore sections that failed to save so polling retries when back online
        latestChangedSectionsRef.current = currentChangedSections;
      });
  };

  // Keep refs updated with latest data for debounced save
  useEffect(() => {
    latestCardsDataRef.current = cardsData;
  }, [cardsData]);

  useEffect(() => {
    latestChangedSectionsRef.current = changedSections;
  }, [changedSections]);

  // Keep saveDataRef updated so debouncedSave always calls the latest saveData
  // (avoids stale closure where loading/viewOnly/jobId etc. are from initial render)
  useEffect(() => {
    saveDataRef.current = saveData;
  });

  // Debounced save function using refs
  const debouncedSave = useCallback(() => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout - uses saveDataRef to always call the latest saveData
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null; // Reset so polling knows save has fired
      if (saveDataRef.current) {
        saveDataRef.current(
          latestCardsDataRef.current,
          latestChangedSectionsRef.current
        );
      }
    }, Number(AUTO_SAVE_DELAY));
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (changeCheckTimeoutRef.current) {
        clearTimeout(changeCheckTimeoutRef.current);
      }
    };
  }, []);

  // Poll for changes every 100ms using setTimeout instead of useEffect dependency chain
  // This prevents infinite re-render loops caused by cascading state updates
  // Only triggers debouncedSave when no save is already scheduled or in progress
  useEffect(() => {
    const checkForChanges = () => {
      if (
        latestChangedSectionsRef.current.length > 0 &&
        !loading &&
        !viewOnly &&
        !viewOnly1
      ) {
        if (!navigator.onLine) {
          setSaveStatus('You are Offline');
        } else if (!saveTimeoutRef.current && !saveInProgressRef.current) {
          dispatch(setUserUnsavedStatus(true));
          debouncedSave();
        }
      }
      changeCheckTimeoutRef.current = setTimeout(checkForChanges, 100);
    };
    changeCheckTimeoutRef.current = setTimeout(checkForChanges, 100);
    return () => {
      if (changeCheckTimeoutRef.current) {
        clearTimeout(changeCheckTimeoutRef.current);
      }
    };
  }, [loading, viewOnly, viewOnly1, debouncedSave, dispatch]);

  // Add new card
  const handleAddCard = () => {
    const newId = `new-${nanoid()}`;
    const newCard = {
      id: newId,
      heading: '',
      systemHeading: 'NONE',
      submitToEmr: true,
      suppressOnEmpty: false,
      paragraphs: '',
      fieldType: 'TEXT',
      options: [],
      selectedValues: null,
    };
    setCardsData(prevCards => [...prevCards, newCard]);
    setChangeSections(prev => [...prev, newId]);
  };

  const handleAddGroupCard = () => {
    const name = newGroupName.trim();
    if (!name) return;
    setCardsData(prevCards => [
      ...prevCards,
      { id: `group-${nanoid()}`, cardType: 'sectionGroup', title: name, children: [] },
    ]);
    setNewGroupName('');
    setAddGroupModalOpen(false);
  };

  const handleAddChildHeading = groupId => {
    const newId = `new-${nanoid()}`;
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : {
        ...card,
        children: [...(card.children || []), {
          id: newId,
          heading: '',
          systemHeading: 'NONE',
          submitToEmr: true,
          suppressOnEmpty: false,
          paragraphs: '',
          fieldType: 'TEXT',
          options: [],
          selectedValues: null,
        }],
      }
    ));
  };

  const handleDeleteGroupChild = (groupId, childId) => {
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : {
        ...card,
        children: (card.children || []).filter(c => c.id !== childId),
      }
    ));
  };

  const handleGroupTitleChange = (groupId, value) => {
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : { ...card, title: value }
    ));
  };

  const handleChildHeadingSelectionChange = (item, groupId, childId) => {
    const fieldType = item.fieldType || 'TEXT';
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : {
        ...card,
        children: (card.children || []).map(child =>
          child.id !== childId ? child : {
            ...child,
            heading: item.customUIHeading.toUpperCase(),
            systemHeading: item.systemHeading,
            fieldType,
            options: item.options || [],
            selectedValues: fieldType === 'CHECKBOX' ? [] : null,
          }
        ),
      }
    ));
    setChangeSections(prev => prev.includes(childId) ? prev : [...prev, childId]);
    setHeadingMenuVisible(null);
  };

  const handleChildParagraphsChange = (groupId, childId, value) => {
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : {
        ...card,
        children: (card.children || []).map(child =>
          child.id !== childId ? child : { ...child, paragraphs: value }
        ),
      }
    ));
    setChangeSections(prev => prev.includes(childId) ? prev : [...prev, childId]);
  };

  const handleSelectedValuesChange = (cardId, values) => {
    setCardsData(prevCards => prevCards.map(card =>
      card.id === cardId ? { ...card, selectedValues: values } : card
    ));
    setChangeSections(prev => prev.includes(cardId) ? prev : [...prev, cardId]);
  };

  const handleChildSelectedValuesChange = (groupId, childId, values) => {
    setCardsData(prevCards => prevCards.map(card =>
      card.id !== groupId ? card : {
        ...card,
        children: (card.children || []).map(child =>
          child.id !== childId ? child : { ...child, selectedValues: values }
        ),
      }
    ));
    setChangeSections(prev => prev.includes(childId) ? prev : [...prev, childId]);
  };

  // Update card heading
  const handleHeadingChange = (cardId, value) => {
    setCardsData(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, heading: value } : card
      )
    );
    setChangeSections(prev => {
      if (!prev.includes(cardId)) {
        return [...prev, cardId];
      }
      return prev;
    });
  };

  // Update card paragraphs
  const handleParagraphsChange = (cardId, value) => {
    setCardsData(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, paragraphs: value } : card
      )
    );
    setChangeSections(prev => {
      if (!prev.includes(cardId)) {
        return [...prev, cardId];
      }
      return prev;
    });
  };

  // Delete card
  const handleDeleteCard = cardId => {
    setCardsData(prevCards => prevCards.filter(card => card.id !== cardId));
    setDeleteConfirmId(null);
    setChangeSections(prev => {
      if (!prev.includes(cardId)) {
        return [...prev, cardId];
      }
      return prev;
    });
  };

  // Show heading menu with dynamic positioning
  const handleShowHeadingMenu = (cardId, event) => {
    const buttonRect = event.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();

    if (containerRect) {
      const spaceBelow = containerRect.bottom - buttonRect.bottom;
      const spaceAbove = buttonRect.top - containerRect.top;
      const menuHeight = 250; // Approximate height of dropdown menu

      // If not enough space below but enough above, open upward
      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }

    setHeadingMenuVisible(cardId);
  };

  // Handle heading selection from menu
  const handleHeadingSelectionChange = (item, cardId) => {
    const fieldType = item.fieldType || 'TEXT';
    setCardsData(prevCards =>
      prevCards.map(card =>
        card.id === cardId
          ? {
              ...card,
              heading: item.customUIHeading.toUpperCase(),
              systemHeading: item.systemHeading,
              fieldType,
              options: item.options || [],
              selectedValues: fieldType === 'CHECKBOX' ? [] : null,
            }
          : card
      )
    );
    setChangeSections(prev => {
      if (!prev.includes(cardId)) {
        return [...prev, cardId];
      }
      return prev;
    });
    setHeadingMenuVisible(null);
  };

  // Save cursor position when selection changes
  const handleSelectionChange = cardId => {
    const editor = contentEditorsRef.current[cardId];
    if (editor) {
      const quill = editor.getEditor();
      const range = quill.getSelection();
      if (range) {
        cursorPositionRef.current[cardId] = range.index;
      }
    }
  };

  // Helper function to convert HTML to text while preserving line breaks
  const htmlToTextWithLineBreaks = html => {
    if (!html) return '';

    let text = html;

    // Replace <br> tags with newlines
    text = text.replace(/<br\s*\/?>/gi, '\n');

    // Replace </p><p> patterns with newlines (paragraph breaks)
    text = text.replace(/<\/p>\s*<p>/gi, '\n');

    // Replace closing </p> tags with newlines (end of paragraph)
    text = text.replace(/<\/p>/gi, '\n');

    // Remove opening <p> tags
    text = text.replace(/<p>/gi, '');

    // Remove any remaining HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    text = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up multiple consecutive newlines (keep max 2)
    text = text.replace(/\n{3,}/g, '\n\n');

    // Trim leading/trailing whitespace but preserve internal newlines
    text = text.trim();

    return text;
  };

  // Preprocess HTML for Quill editor to handle line breaks correctly
  // - </p><br><p> should be single line break (remove redundant <br>)
  // - </p><br><br><p> should be blank line (keep one <br> as empty paragraph)
  const preprocessHtmlForQuill = html => {
    if (!html) return '';

    let processed = html;

    // First, handle double <br><br> - convert to empty paragraph for blank line
    // </p><br><br><p> → </p><p><br></p><p> (creates visible blank line)
    processed = processed.replace(
      /<\/p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<p>/gi,
      '</p><p><br></p><p>'
    );

    // Then, remove single <br> between paragraphs (paragraphs already have line breaks)
    // </p><br><p> → </p><p>
    processed = processed.replace(/<\/p>\s*<br\s*\/?>\s*<p>/gi, '</p><p>');

    return processed;
  };

  // Insert key phrase at cursor position (from dropdown or type-to-expand)
  const handleKeyPhraseSelect = (phraseId, cardId, replaceLength = 0) => {
    const editor = contentEditorsRef.current[cardId];
    if (editor) {
      const quill = editor.getEditor();

      // Look up the full K2P object using the id
      const k2pItem = keyPhrasesDataRef.current[phraseId];
      if (!k2pItem) return;

      // Process dynamic placeholders if needed
      const processedPhrase = processDynamicPlaceholders(k2pItem);

      // Preprocess HTML for proper Quill rendering
      const quillReadyHtml = preprocessHtmlForQuill(processedPhrase);

      // Use the last saved cursor position, or default to end of content
      let position =
        cursorPositionRef.current[cardId] !== undefined
          ? cursorPositionRef.current[cardId]
          : quill.getLength() - 1;

      // If we need to replace the typed key (type-to-expand), delete it first
      if (replaceLength > 0) {
        const deleteStart = position - replaceLength;
        quill.deleteText(deleteStart, replaceLength);
        position = deleteStart;
      }

      // For dynamic K2Ps, first insert a newline to ensure content starts on new line
      // (only if we're not at the start of the editor)
      if (k2pItem.dynamic && k2pItem.targetEndPoint && position > 0) {
        // Check if we're already at the start of a new line
        const textBefore = quill.getText(0, position);
        const lastChar = textBefore.charAt(textBefore.length - 1);
        if (lastChar !== '\n') {
          quill.insertText(position, '\n');
          position += 1;
        }
      }

      // Insert HTML directly using Quill's clipboard to preserve formatting
      quill.setSelection(position, 0);
      quill.clipboard.dangerouslyPasteHTML(position, quillReadyHtml);

      // Get the new length after HTML insertion
      const newLength = quill.getLength();

      // Focus and set cursor after the inserted content
      quill.focus();
      quill.setSelection(newLength - 1);

      // Update the stored cursor position
      cursorPositionRef.current[cardId] = newLength - 1;
    }
  };

  // Handle type-to-expand K2P (checks for matching key when space/tab is pressed)
  const handleTypeToExpand = (cardId, event) => {
    const editor = contentEditorsRef.current[cardId];
    if (!editor) return false;

    const quill = editor.getEditor();
    const selection = quill.getSelection();
    if (!selection) return false;

    const cursorIndex = selection.index;

    // Get text before cursor
    const textBeforeCursor = quill.getText(0, cursorIndex);

    // Find the last word (text after last space or newline)
    const lastSpaceIndex = Math.max(
      textBeforeCursor.lastIndexOf(' '),
      textBeforeCursor.lastIndexOf('\n'),
      textBeforeCursor.lastIndexOf('\t')
    );

    const lastWord =
      lastSpaceIndex === -1
        ? textBeforeCursor.trim()
        : textBeforeCursor.substring(lastSpaceIndex + 1).trim();

    if (!lastWord) return false;

    // Check if the last word matches any K2P key (case-insensitive)
    const matchedK2P = keyPhrasesMapRef.current[lastWord.toLowerCase()];

    if (matchedK2P) {
      event.preventDefault();

      // Update cursor position to current position
      cursorPositionRef.current[cardId] = cursorIndex;

      // Insert the K2P phrase, replacing the typed key
      handleKeyPhraseSelect(matchedK2P.id, cardId, lastWord.length);
      return true;
    }

    return false;
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    saveData: () => {
      const allSectionIds = cardsData.flatMap(card =>
        card.cardType === 'sectionGroup'
          ? (card.children || []).map(c => c.id)
          : [card.id]
      );
      saveData(cardsData, allSectionIds);
    },
    getCurrentData: () => {
      const buildEntry = (c, groupInfo = {}) => {
        const isCheckbox = c.fieldType === 'CHECKBOX';
        const isDropdown = c.fieldType === 'DROPDOWN';
        const section = {
          id: c.id,
          heading: {
            customUIHeading: convertHtmlToPlainWithBr(c.heading),
            systemHeading: c.systemHeading || 'NONE',
            submitToEmr: c.submitToEmr,
            suppressOnEmpty: c.suppressOnEmpty ?? false,
            fieldType: c.fieldType || 'TEXT',
            ...(c.headingId && { headingId: c.headingId }),
            ...(groupInfo.groupId && { groupId: groupInfo.groupId, groupName: groupInfo.groupName || null }),
          },
          content: [
            {
              type: 'text',
              value: isCheckbox || isDropdown ? '' : convertHtmlToPlainWithBr(c.paragraphs)
            }
          ]
        };
        if (isCheckbox) {
          section.options = (c.options || []).map(o => {
            const label = typeof o === 'string' ? o : o.label;
            return { label, selected: (c.selectedValues || []).includes(label) };
          });
        } else if (isDropdown) {
          section.options = (c.options || []).map(o => {
            const label = typeof o === 'string' ? o : o.label;
            return { label, selected: label === c.selectedValues };
          });
        }
        return section;
      };
      return cardsData.flatMap(card =>
        card.cardType === 'sectionGroup'
          ? (card.children || []).map(child => buildEntry(child, { groupId: card.id, groupName: card.title }))
          : [buildEntry(card)]
      );
    },
    getContentText: () => {
      const cardText = c => {
        const heading = c.heading ? c.heading.replace(/<[^>]*>/g, '').trim() : '';
        const body = c.paragraphs ? c.paragraphs.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>\s*<p>/gi, '\n').replace(/<[^>]*>/g, '').trim() : '';
        return heading ? `${heading}\n${body}` : body;
      };
      return cardsData
        .flatMap(card =>
          card.cardType === 'sectionGroup'
            ? (card.children || []).map(cardText)
            : [cardText(card)]
        )
        .filter(Boolean)
        .join('\n\n');
    },
    handleUndo: () => {},
    handleRedo: () => {},
    headingHandler: () => {}
  }));

  // Generate modules config for each card with unique toolbar
  const getModules = cardId => ({
    toolbar: {
      container: `#toolbar-${cardId}`
    },
    clipboard: {
      matchVisual: false
    }
  });

  const modulesReadOnly = {
    toolbar: false,
    clipboard: {
      matchVisual: false
    }
  };

  // const formats = ['bold', 'italic', 'strike', 'underline'];
  const formats = [];

  return (
    <div
      ref={containerRef}
      style={{
        border: '1px solid #ddd',
        borderRadius: '6px',
        padding: '8px',
        height: 'calc(100vh - 200px)',
        overflowY: 'auto'
      }}
    >
      {/* Cards Container */}
      {cardsData.map(card => {
        if (card.cardType === 'sectionGroup') {
          return (
            <div
              key={card.id}
              style={{
                marginBottom: '15px',
                border: '1px solid #d0e8ff',
                borderRadius: '8px',
                background: '#f6fbff',
                overflow: 'hidden',
              }}
            >
              {/* Group header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: '#e8f4ff',
                borderBottom: '1px solid #d0e8ff',
              }}>
                {viewOnly || viewOnly1 ? (
                  <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 14 }}>{card.title}</span>
                ) : (
                  <Input
                    value={card.title}
                    onChange={e => handleGroupTitleChange(card.id, e.target.value)}
                    style={{ fontWeight: 600, color: '#1677ff', fontSize: 14, border: 'none', background: 'transparent', boxShadow: 'none', padding: '0 4px', width: 'auto', minWidth: 120, flex: 1 }}
                    onClick={e => e.stopPropagation()}
                  />
                )}
                {!viewOnly && !viewOnly1 && (
                  <Button
                    type='text'
                    size='small'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => setDeleteGroupConfirmId(card.id)}
                  />
                )}
              </div>
              {/* Child heading cards */}
              <div style={{ padding: '12px 12px 4px' }}>
                {(card.children || []).map(child => (
                  <Card
                    key={child.id}
                    style={{ marginBottom: '10px', border: '1px solid #d9d9d9' }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <label style={{ fontWeight: 'bold', fontSize: 13 }}>Heading:</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {!viewOnly && !viewOnly1 && (
                          <>
                            {/* <div style={{ position: 'relative' }}>
                              <Button size='small' icon={<CaretDownOutlined />} onClick={e => handleShowHeadingMenu(child.id, e)}>
                                Select Heading
                              </Button>
                              {headingMenuVisible === child.id && (
                                <div className='floating-menu-container' style={{
                                  position: 'absolute',
                                  ...(dropdownPosition === 'bottom' ? { top: '100%', marginTop: 5 } : { bottom: '100%', marginBottom: 5 }),
                                  right: 0,
                                  zIndex: 1000,
                                }}>
                                  <FloatingMenu
                                    cursorPosition={{ top: 0, right: 0 }}
                                    headings={headings}
                                    handleHeadingChange={item => handleChildHeadingSelectionChange(item, card.id, child.id)}
                                  />
                                </div>
                              )}
                            </div> */}
                            <Button size='small' type='text' danger icon={<DeleteOutlined />} onClick={() => setDeleteChildConfirm({ groupId: card.id, childId: child.id })} />
                          </>
                        )}
                      </div>
                    </div>
                    <ReactQuill
                      value={child.heading}
                      onChange={value => {
                        setCardsData(prev => prev.map(c =>
                          c.id !== card.id ? c : {
                            ...c,
                            children: (c.children || []).map(ch => ch.id === child.id ? { ...ch, heading: value } : ch),
                          }
                        ));
                        setChangeSections(prev => prev.includes(child.id) ? prev : [...prev, child.id]);
                      }}
                      modules={{ toolbar: false }}
                      theme='bubble'
                      placeholder='Enter heading...'
                      readOnly={viewOnly || viewOnly1}
                      style={{ fontSize: 18, fontWeight: 600, color: '#47A4EA', minHeight: 36 }}
                    />
                    {child.fieldType === 'CHECKBOX' ? (
                      <Checkbox.Group
                        options={(child.options || []).map(o => { const label = typeof o === 'string' ? o : o.label; return { label, value: label }; })}
                        value={child.selectedValues || []}
                        onChange={values => handleChildSelectedValuesChange(card.id, child.id, values)}
                        style={{ marginTop: 8 }}
                        disabled={viewOnly || viewOnly1}
                      />
                    ) : child.fieldType === 'DROPDOWN' ? (
                      <Select
                        style={{ width: '100%', marginTop: 8 }}
                        placeholder='Select an option'
                        options={(child.options || []).map(o => { const label = typeof o === 'string' ? o : o.label; return { label, value: label }; })}
                        value={child.selectedValues}
                        onChange={value => handleChildSelectedValuesChange(card.id, child.id, value)}
                        disabled={viewOnly || viewOnly1}
                      />
                    ) : (
                      <ReactQuill
                        value={child.paragraphs}
                        onChange={value => handleChildParagraphsChange(card.id, child.id, value)}
                        modules={{ toolbar: false }}
                        formats={formats}
                        theme='snow'
                        placeholder='Enter content...'
                        readOnly={viewOnly || viewOnly1}
                        style={{ fontSize: 14, fontFamily: "'Krub', sans-serif", minHeight: 80 }}
                      />
                    )}
                  </Card>
                ))}
                {!viewOnly && !viewOnly1 && (
                  <Button
                    type='dashed'
                    icon={<PlusOutlined />}
                    style={{ width: '100%', marginBottom: 10, borderColor: '#91caff', color: '#4096ff', height: 40 }}
                    onClick={() => handleAddChildHeading(card.id)}
                  >
                    Add Heading
                  </Button>
                )}
              </div>
            </div>
          );
        }

        return (
        <Card
          key={card.id}
          style={{
            marginBottom: '15px',
            border:
              activeSection === card.id
                ? '2px solid #47A4EA'
                : '1px solid #d9d9d9',
            cursor: 'pointer'
          }}
          bodyStyle={{ padding: '15px' }}
          onClick={() => setActiveSection(card.id)}
        >
          <div style={{ position: 'relative' }}>
            <div style={{ marginBottom: '10px' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '5px'
                }}
              >
                <label style={{ fontWeight: 'bold' }}>Heading:</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!viewOnly && !viewOnly1 && (
                    <>
                      {/* <div style={{ position: 'relative' }}>
                        <Button
                          size='small'
                          icon={<CaretDownOutlined />}
                          onClick={e => handleShowHeadingMenu(card.id, e)}
                        >
                          Select Heading
                        </Button>
                        {headingMenuVisible === card.id && (
                          <div
                            className='floating-menu-container'
                            style={{
                              position: 'absolute',
                              ...(dropdownPosition === 'bottom'
                                ? { top: '100%', marginTop: '5px' }
                                : { bottom: '100%', marginBottom: '5px' }),
                              right: 0,
                              zIndex: 1000
                            }}
                          >
                            <FloatingMenu
                              cursorPosition={{ top: 0, right: 0 }}
                              headings={headings}
                              handleHeadingChange={item =>
                                handleHeadingSelectionChange(item, card.id)
                              }
                            />
                          </div>
                        )}
                      </div> */}
                      <Button
                        size='small'
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setDeleteConfirmId(card.id)}
                      />
                    </>
                  )}
                </div>
              </div>
              <ReactQuill
                value={card.heading}
                onChange={value => handleHeadingChange(card.id, value)}
                modules={{ toolbar: false }}
                theme='bubble'
                placeholder='Enter heading...'
                readOnly={viewOnly || viewOnly1}
                style={{
                  fontSize: '21px',
                  fontWeight: '600',
                  color: '#47A4EA',
                  fontFamily: "'Krub', sans-serif",
                  minHeight: '40px'
                }}
              />
            </div>

            {card.fieldType === 'CHECKBOX' ? (
              <Checkbox.Group
                options={(card.options || []).map(o => { const label = typeof o === 'string' ? o : o.label; return { label, value: label }; })}
                value={card.selectedValues || []}
                onChange={values => handleSelectedValuesChange(card.id, values)}
                style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}
                disabled={viewOnly || viewOnly1}
              />
            ) : card.fieldType === 'DROPDOWN' ? (
              <Select
                style={{ width: '100%', marginTop: 8 }}
                placeholder='Select an option'
                options={(card.options || []).map(o => { const label = typeof o === 'string' ? o : o.label; return { label, value: label }; })}
                value={card.selectedValues}
                onChange={value => handleSelectedValuesChange(card.id, value)}
                disabled={viewOnly || viewOnly1}
              />
            ) : (
              <div style={{ marginBottom: '10px' }}>
                <label
                  style={{
                    fontWeight: 'bold',
                    display: 'block',
                    marginBottom: '5px'
                  }}
                >
                  Content:
                </label>

                {/* Custom Toolbar with Key Phrase dropdown */}
                {!viewOnly && !viewOnly1 && (
                  <div
                    id={`toolbar-${card.id}`}
                    className='custom-quill-toolbar'
                    style={{ position: 'relative' }}
                  >
                    {/* <button className='ql-bold' />
                    <button className='ql-italic' />
                    <button className='ql-underline' />
                    <button className='ql-strike' /> */}
                    {(() => {
                      const headingPlain = card.heading
                        .replace(/<[^>]*>/g, '')
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>')
                        .trim();
                      const useAI = headings?.find(
                        h => h.customUIHeading.toLowerCase() === headingPlain.toLowerCase()
                      )?.useAI;
                      if (!useAI) return null;

                      const cached = aiSectionCache[headingPlain.toLowerCase()];
                      let statusIndicator = null;
                      if (cached) {
                        if (cached.status === 'PENDING') {
                          statusIndicator = (
                            <Tooltip title='AI generation in progress…'>
                              <LoadingOutlined style={{ color: '#47a4ec', fontSize: 16, marginLeft: 6 }} />
                            </Tooltip>
                          );
                        } else if (cached.status === 'COMPLETED' && !cached.added) {
                          statusIndicator = (
                            <Tooltip title='AI content ready — click AI Generate to review'>
                              <ClockCircleOutlined style={{ color: '#faad14', fontSize: 16, marginLeft: 6 }} />
                            </Tooltip>
                          );
                        } else if (cached.status === 'COMPLETED' && cached.added) {
                          statusIndicator = (
                            <Tooltip title='AI content already applied'>
                              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16, marginLeft: 6 }} />
                            </Tooltip>
                          );
                        } else if (cached.status === 'FAILED') {
                          statusIndicator = (
                            <Tooltip title='AI generation failed — click AI Generate to retry'>
                              <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16, marginLeft: 6 }} />
                            </Tooltip>
                          );
                        }
                      }

                      return (
                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                          <Button
                            type='primary'
                            shape='round'
                            size='small'
                            onClick={() => handleAiGenerate(card)}
                          >
                            AI Generate
                          </Button>
                          {statusIndicator}
                        </span>
                      );
                    })()}
                    {card.submitToEmr === false && (
                      <span
                        style={{
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#47b7f4',
                          backgroundColor: '#e8f7ff',
                          border: '1px solid #47b7f4',
                          borderRadius: '4px',
                          padding: '1px 8px',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none'
                        }}
                      >
                        Suppressed from EHR
                      </span>
                    )}
                    {!isIncomplete && (
                      <Select
                        placeholder='Insert Key Phrase'
                        style={{
                          width: 180,
                          marginLeft: 'auto'
                        }}
                        className='toolbar-key-phrase-select'
                        onChange={value => handleKeyPhraseSelect(value, card.id)}
                        options={getFilteredKeyPhrasesForCard(card.heading)}
                        value={null}
                        size='small'
                        loading={k2pApisLoading}
                        disabled={k2pApisLoading}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                    )}
                  </div>
                )}

                <div
                  onKeyDown={e => {
                    // Type-to-expand disabled - TRPA-6192
                  }}
                >
                  <ReactQuill
                    ref={el => {
                      if (el) {
                        contentEditorsRef.current[card.id] = el;
                      }
                    }}
                    value={card.paragraphs}
                    onChange={value => handleParagraphsChange(card.id, value)}
                    onChangeSelection={() => handleSelectionChange(card.id)}
                    modules={
                      !viewOnly && !viewOnly1
                        ? getModules(card.id)
                        : modulesReadOnly
                    }
                    formats={formats}
                    theme='snow'
                    placeholder='Enter content...'
                    readOnly={viewOnly || viewOnly1}
                    style={{
                      fontSize: '14px',
                      fontFamily: "'Krub', sans-serif",
                      minHeight: '100px'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
        );
      })}

      {/* Add Button */}
      {!viewOnly && !viewOnly1 && (
        <div style={{ marginTop: '10px' }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <Menu>
                <Menu.Item key='heading' icon={<PlusOutlined />} onClick={handleAddCard}>
                  Heading
                </Menu.Item>
                <Menu.Item key='section-group' icon={<PlusOutlined />} onClick={() => { setNewGroupName(''); setAddGroupModalOpen(true); }}>
                  Section Group
                </Menu.Item>
              </Menu>
            }
          >
            <Button type='dashed' icon={<PlusOutlined />} style={{ width: '100%' }}>
              Add Section <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
            </Button>
          </Dropdown>
        </div>
      )}

      {/* Section Group creation modal */}
      <Modal
        title='Add section group'
        visible={addGroupModalOpen}
        wrapClassName='template-new-modal'
        onCancel={() => setAddGroupModalOpen(false)}
        onOk={handleAddGroupCard}
        okText='Add section group'
        okButtonProps={{ disabled: !newGroupName.trim(), style: { borderRadius: 6 } }}
        cancelButtonProps={{ style: { borderRadius: 6 } }}
        width={400}
        style={{ top: 200 }}
        destroyOnClose
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#8c8c8c', marginBottom: 4 }}>
            Section group title <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
          <Input
            placeholder='e.g. CPT Codes'
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onPressEnter={handleAddGroupCard}
            autoFocus
            style={{ borderRadius: 6 }}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal — standalone section */}
      <ConfirmationModal
        visible={deleteConfirmId !== null}
        onOk={() => {
          handleDeleteCard(deleteConfirmId);
        }}
        onCancel={() => {
          setDeleteConfirmId(null);
        }}
        confirmation={true}
        antdModalProps={{ closable: false }}
      >
        <WarningIcon />
        <p style={{ fontSize: '16px' }}>
          Are you sure you want to delete this section?
        </p>
      </ConfirmationModal>

      {/* Delete Confirmation Modal — section group */}
      <ConfirmationModal
        visible={deleteGroupConfirmId !== null}
        onOk={() => {
          setCardsData(prev => prev.filter(c => c.id !== deleteGroupConfirmId));
          setDeleteGroupConfirmId(null);
        }}
        onCancel={() => setDeleteGroupConfirmId(null)}
        confirmation={true}
        antdModalProps={{ closable: false }}
      >
        <WarningIcon />
        <p style={{ fontSize: '16px' }}>
          Are you sure you want to delete this section group?
        </p>
      </ConfirmationModal>

      {/* Delete Confirmation Modal — heading inside section group */}
      <ConfirmationModal
        visible={deleteChildConfirm !== null}
        onOk={() => {
          if (deleteChildConfirm) {
            handleDeleteGroupChild(deleteChildConfirm.groupId, deleteChildConfirm.childId);
          }
          setDeleteChildConfirm(null);
        }}
        onCancel={() => setDeleteChildConfirm(null)}
        confirmation={true}
        antdModalProps={{ closable: false }}
      >
        <WarningIcon />
        <p style={{ fontSize: '16px' }}>
          Are you sure you want to delete this section?
        </p>
      </ConfirmationModal>
      <Modal
        visible={aiModalState.visible}
        title={<span style={{ color: '#47a4ec' }}>AI Generate</span>}
        className='ai-generate-modal'
        onCancel={() =>
          setAiModalState({
            visible: false,
            cardId: null,
            heading: null,
            loading: false,
            result: null
          })
        }
        footer={
          aiModalState.loading
            ? [
                <Button
                  key='run-in-bg'
                  type='primary'
                  onClick={() =>
                    setAiModalState({
                      visible: false,
                      cardId: null,
                      heading: null,
                      loading: false,
                      result: null
                    })
                  }
                >
                  Run in Background
                </Button>
              ]
            : aiAcknowledged === null
            ? null
            : aiAcknowledged === false
            ? [
                <Checkbox
                  key='ack-checkbox'
                  checked={aiAckChecked}
                  onChange={e => setAiAckChecked(e.target.checked)}
                  style={{ fontSize: 13, fontWeight: 500, color: '#47a4ec' }}
                  className='ai-ack-checkbox'
                >
                  I acknowledge that AI-generated content is assistive and must
                  be reviewed before submission.
                </Checkbox>,
                <Button
                  key='acknowledge'
                  type='primary'
                  disabled={!aiAckChecked}
                  loading={aiAckLoading}
                  onClick={() => {
                    setAiAckLoading(true);
                    acknowledgeAiGeneration(jobId, user.sessionId)
                      .then(() => {
                        setAiAcknowledged(true);
                        setAiGlobalAcknowledged(true);
                        setAiAckLoading(false);
                      })
                      .catch(() => {
                        setAiAckLoading(false);
                      });
                  }}
                >
                  Acknowledge
                </Button>
              ]
            : [
                <Button
                  key='copy-to-content'
                  type='primary'
                  onClick={() => {
                    const fullText = aiModalState.result?.result?.[0] || '';
                    const cardId = aiModalState.cardId;
                    const heading = aiModalState.heading;
                    if (fullText && cardId) {
                      const updatedCards = cardsData.map(c =>
                        c.id === cardId
                          ? { ...c, paragraphs: fullText.replace(/\n/g, '<br>') }
                          : c
                      );
                      const newChangedSections = changedSections.includes(cardId)
                        ? changedSections
                        : [...changedSections, cardId];
                      setCardsData(updatedCards);
                      setChangeSections(newChangedSections);
                      if (heading) {
                        markAiSectionAsAdded(jobId, heading)
                          .then(() => saveDataRef.current(updatedCards, newChangedSections))
                          .catch(() => {});
                        setAiSectionCache(prev => ({
                          ...prev,
                          [heading.toLowerCase()]: {
                            ...(prev[heading.toLowerCase()] || {}),
                            added: true
                          }
                        }));
                      }
                    }
                    setAiModalState({
                      visible: false,
                      cardId: null,
                      heading: null,
                      loading: false,
                      result: null
                    });
                  }}
                >
                  Copy to Content
                </Button>
              ]
        }
        centered
        destroyOnClose
      >
        {aiModalState.loading ? (
          <div
            style={{
              padding: '16px 0',
              fontSize: 16,
              color: '#47a4ec',
              textAlign: 'center'
            }}
          >
            {'Thinking' + '.'.repeat(aiDots)}
          </div>
        ) : (
          <div
            style={{
              padding: '8px 0',
              fontSize: 14,
              color: '#262626',
              whiteSpace: 'pre-wrap',
              userSelect: 'none',
              maxHeight: '60vh',
              overflowY: 'auto'
            }}
          >
            {aiDisplayedText}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default forwardRef(QuillEditor);
